import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiInfoBanner, NoddiTextArea, noddiToast } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';

interface EditCommentProps {
  commentsUser: string;
  bookingId: number;
  bookingSlug: string;
}

export const EditComment = ({ commentsUser, bookingId, bookingSlug }: EditCommentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [userComment, setUserComment] = useState(commentsUser);
  const { isLoggedIn } = useAuthContext();

  const [hasTriggeredEditComment, setHasTriggeredEditComment] = useState(false);

  const {
    mutateAsync: updateBooking,
    isPending: isUpdateBookingLoading,
    isError
  } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getBookingInfo,
          input: { slug: bookingSlug }
        });
        setIsEditing(false);
        noddiToast.success(t`Your comment has been updated`);
      }
    }
  });

  return (
    <div className='flex flex-col gap-2'>
      <NoddiTextArea
        className='bg-white'
        value={userComment}
        onChange={(newValue) => setUserComment(newValue.target.value)}
        onFocus={() => {
          if (isLoggedIn) {
            setIsEditing(true);
          } else {
            setHasTriggeredEditComment(true);
          }
        }}
        placeholder={t`Optional note if any extra information is needed like parking info etc.`}
      />

      {isEditing && (
        <>
          {isError && (
            <NoddiInfoBanner
              texts={[{ value: t`We had trouble updating your booking. Please try again` }]}
              variant='error'
            />
          )}
          <div className='flex justify-end gap-2'>
            <NoddiButton size='extraSmall' variant='outline' onClick={() => setIsEditing(false)}>
              <Trans>Cancel</Trans>
            </NoddiButton>
            <NoddiButton
              size='extraSmall'
              loading={isUpdateBookingLoading}
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs updateBooking(); React discards the returned promise, so a rejection is unhandled unless the body catches
              onClick={async () => {
                await updateBooking({
                  bookingId,
                  commentsUser: userComment
                });
              }}
            >
              <Trans>Save</Trans>
            </NoddiButton>
          </div>
        </>
      )}

      <CustomerLoginModal
        open={hasTriggeredEditComment}
        onClose={() => setHasTriggeredEditComment(false)}
        title={t`Sign in`}
        description={t`Please sign in order to edit your comment`}
        onLoginSuccess={() => {
          setIsEditing(true);
          setHasTriggeredEditComment(false);
        }}
      />
    </div>
  );
};
